import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
/**
 * DayCaseObservation.js
 *
 * Outpatient post-procedure monitoring page. Tracks serial vitals, pain scores,
 * consciousness, wound status, nausea/vomiting, and oral fluid tolerance over
 * pre-populated time intervals (15 min, 30 min, 60 min).
 *
 * Props:
 *   surgeryId – unique identifier for the current surgery
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Slider from '@material-ui/core/Slider';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import TimerIcon from '@material-ui/icons/Timer';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { surgeryService } from '../../../services/surgeryService';

const useStyles = makeStyles(() => ({
  root: {
    padding: 24,
  },
  pageTitle: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#333',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: '0.88rem',
    color: '#666',
    marginBottom: 24,
  },
  section: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 8,
    border: '1px solid #e0e0e0',
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: 16,
  },
  timerBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    backgroundColor: '#E3F2FD',
    borderRadius: 8,
    marginBottom: 20,
  },
  timerValue: {
    fontSize: '1.6rem',
    fontWeight: 700,
    fontFamily: 'monospace',
    color: '#1565C0',
  },
  timerLabel: {
    fontSize: '0.88rem',
    fontWeight: 600,
    color: '#1565C0',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fafafa',
    padding: '10px 12px',
    borderRadius: '8px 8px 0 0',
    border: '1px solid #e0e0e0',
    borderBottom: 'none',
  },
  headerCell: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  vitalsRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderTop: 'none',
    '&:last-child': {
      borderRadius: '0 0 8px 8px',
    },
  },
  input: {
    '& input': {
      padding: '8px 10px',
      fontSize: '0.85rem',
    },
  },
  selectSmall: {
    fontSize: '0.85rem',
    '& .MuiSelect-select': {
      padding: '8px 10px',
    },
  },
  assessmentGrid: {
    marginBottom: 16,
  },
  addBtn: {
    marginTop: 12,
    marginBottom: 16,
  },
  actionBtnRow: {
    display: 'flex',
    gap: 16,
    marginTop: 24,
  },
  dischargeBtn: {
    fontWeight: 600,
    backgroundColor: '#2E7D32',
    color: '#fff',
    '&:hover': { backgroundColor: '#1B5E20' },
  },
  escalateBtn: {
    fontWeight: 600,
    backgroundColor: '#D32F2F',
    color: '#fff',
    '&:hover': { backgroundColor: '#B71C1C' },
  },
  escalateWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    backgroundColor: '#FFF3E0',
    borderRadius: 8,
    marginTop: 12,
  },
  sliderContainer: {
    paddingLeft: 8,
    paddingRight: 8,
  },
  painLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    color: '#888',
  },
}));

const formatTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString('en-UG', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const formatElapsed = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const PAIN_MARKS = [
  { value: 0, label: '0' },
  { value: 5, label: '5' },
  { value: 10, label: '10' },
];

const CONSCIOUSNESS_OPTIONS = ['Alert', 'Drowsy', 'Unresponsive'];
const WOUND_OPTIONS = ['Dry and intact', 'Oozing', 'Bleeding — escalate'];
const NAUSEA_OPTIONS = ['None', 'Mild', 'Moderate', 'Severe'];
const FLUID_RESULT_OPTIONS = ['Tolerated', 'Not tolerated'];

const createObservationRow = (offsetMinutes, startTime) => {
  const time = new Date(startTime.getTime() + offsetMinutes * 60000);
  return {
    id: Date.now() + Math.random(),
    timestamp: formatTime(time),
    rawTime: time.toISOString(),
    heartRate: '',
    systolic: '',
    diastolic: '',
    spo2: '',
    respiratoryRate: '',
    temperature: '',
    painScore: 0,
    consciousness: 'Alert',
    notes: '',
  };
};

const DayCaseObservation = ({ surgeryId }) => {
  const classes = useStyles();

  const [startTime] = useState(() => new Date());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [rows, setRows] = useState(() => {
    const now = new Date();
    return [
      createObservationRow(15, now),
      createObservationRow(30, now),
      createObservationRow(60, now),
    ];
  });
  const [woundStatus, setWoundStatus] = useState('Dry and intact');
  const [nauseaStatus, setNauseaStatus] = useState('None');
  const [fluidOfferedTime, setFluidOfferedTime] = useState('');
  const [fluidResult, setFluidResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [readyForDischarge, setReadyForDischarge] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Elapsed time timer
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleRowChange = useCallback((index, field, value) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const handleDeleteRow = useCallback((index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddRow = () => {
    const newRow = {
      id: Date.now() + Math.random(),
      timestamp: formatTime(new Date()),
      rawTime: new Date().toISOString(),
      heartRate: '',
      systolic: '',
      diastolic: '',
      spo2: '',
      respiratoryRate: '',
      temperature: '',
      painScore: 0,
      consciousness: 'Alert',
      notes: '',
    };
    setRows((prev) => [...prev, newRow]);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await surgeryService.saveDayCaseObs({
        surgery_id: surgeryId,
        observations: rows.map((r) => ({
          timestamp: r.rawTime,
          vitals: {
            heartRate: r.heartRate,
            systolic: r.systolic,
            diastolic: r.diastolic,
            spo2: r.spo2,
            respiratoryRate: r.respiratoryRate,
            temperature: r.temperature,
          },
          painScore: r.painScore,
          consciousness: r.consciousness,
          notes: r.notes,
        })),
        wound_status: woundStatus,
        nausea_status: nauseaStatus,
        fluid_offered_time: fluidOfferedTime,
        fluid_result: fluidResult,
        total_observation_seconds: elapsedSeconds,
      });
      const data = res.data || res;
      showSnackbar(data.message || 'Observations saved successfully');
    } catch (err) {
      showSnackbar(err.message || 'Failed to save observations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const needsEscalation = useMemo(() => {
    return woundStatus === 'Bleeding — escalate' || nauseaStatus === 'Severe';
  }, [woundStatus, nauseaStatus]);

  return (
    <div className={classes.root}>
      <Typography className={classes.pageTitle}>Day-Case Observation</Typography>
      <Typography className={classes.subtitle}>
        Post-procedure monitoring &mdash; Surgery #{surgeryId}
      </Typography>

      {/* Observation Timer */}
      <div className={classes.timerBanner}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <TimerIcon style={{ color: '#1565C0' }} />
          <div>
            <Typography className={classes.timerLabel}>Total Observation Time</Typography>
            <Typography style={{ fontSize: '0.78rem', color: '#666' }}>
              Started at {formatTime(startTime)}
            </Typography>
          </div>
        </div>
        <Typography className={classes.timerValue}>{formatElapsed(elapsedSeconds)}</Typography>
      </div>

      {/* Vitals Table */}
      <Paper className={classes.section} elevation={0}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
            Vital Signs &amp; Assessment
          </Typography>
          <Typography style={{ fontSize: '0.78rem', color: '#888' }}>
            {rows.length} reading{rows.length !== 1 ? 's' : ''}
          </Typography>
        </div>

        {/* Table Header */}
        <div className={classes.tableHeader}>
          <div style={{ width: '8%' }}><Typography className={classes.headerCell}>Time</Typography></div>
          <div style={{ width: '8%' }}><Typography className={classes.headerCell}>HR</Typography></div>
          <div style={{ width: '10%' }}><Typography className={classes.headerCell}>BP</Typography></div>
          <div style={{ width: '8%' }}><Typography className={classes.headerCell}>SpO2</Typography></div>
          <div style={{ width: '7%' }}><Typography className={classes.headerCell}>RR</Typography></div>
          <div style={{ width: '8%' }}><Typography className={classes.headerCell}>Temp</Typography></div>
          <div style={{ width: '14%' }}><Typography className={classes.headerCell}>Pain (0-10)</Typography></div>
          <div style={{ width: '12%' }}><Typography className={classes.headerCell}>Consciousness</Typography></div>
          <div style={{ width: '20%' }}><Typography className={classes.headerCell}>Notes</Typography></div>
          <div style={{ width: '5%' }} />
        </div>

        {/* Rows */}
        {rows.map((row, index) => (
          <div className={classes.vitalsRow} key={row.id}>
            <div style={{ width: '8%' }}>
              <Typography style={{ fontSize: '0.82rem', fontWeight: 500, color: '#333' }}>
                {row.timestamp}
              </Typography>
            </div>
            <div style={{ width: '8%', paddingRight: 4 }}>
              <TextField
                className={classes.input}
                variant="outlined"
                size="small"
                placeholder="bpm"
                value={row.heartRate}
                onChange={(e) => handleRowChange(index, 'heartRate', e.target.value)}
                fullWidth
                type="number"
                disabled={loading}
              />
            </div>
            <div style={{ width: '10%', paddingRight: 4 }}>
              <div style={{ display: 'flex', gap: 2 }}>
                <TextField
                  className={classes.input}
                  variant="outlined"
                  size="small"
                  placeholder="Sys"
                  value={row.systolic}
                  onChange={(e) => handleRowChange(index, 'systolic', e.target.value)}
                  style={{ width: '50%' }}
                  type="number"
                  disabled={loading}
                />
                <TextField
                  className={classes.input}
                  variant="outlined"
                  size="small"
                  placeholder="Dia"
                  value={row.diastolic}
                  onChange={(e) => handleRowChange(index, 'diastolic', e.target.value)}
                  style={{ width: '50%' }}
                  type="number"
                  disabled={loading}
                />
              </div>
            </div>
            <div style={{ width: '8%', paddingRight: 4 }}>
              <TextField
                className={classes.input}
                variant="outlined"
                size="small"
                placeholder="%"
                value={row.spo2}
                onChange={(e) => handleRowChange(index, 'spo2', e.target.value)}
                fullWidth
                type="number"
                disabled={loading}
              />
            </div>
            <div style={{ width: '7%', paddingRight: 4 }}>
              <TextField
                className={classes.input}
                variant="outlined"
                size="small"
                placeholder="/min"
                value={row.respiratoryRate}
                onChange={(e) => handleRowChange(index, 'respiratoryRate', e.target.value)}
                fullWidth
                type="number"
                disabled={loading}
              />
            </div>
            <div style={{ width: '8%', paddingRight: 4 }}>
              <TextField
                className={classes.input}
                variant="outlined"
                size="small"
                placeholder="°C"
                value={row.temperature}
                onChange={(e) => handleRowChange(index, 'temperature', e.target.value)}
                fullWidth
                type="number"
                disabled={loading}
              />
            </div>
            <div style={{ width: '14%', paddingRight: 8 }}>
              <div className={classes.sliderContainer}>
                <Slider
                  value={row.painScore}
                  onChange={(_, val) => handleRowChange(index, 'painScore', val)}
                  min={0}
                  max={10}
                  step={1}
                  marks={PAIN_MARKS}
                  valueLabelDisplay="auto"
                  disabled={loading}
                />
              </div>
            </div>
            <div style={{ width: '12%', paddingRight: 4 }}>
              <Select
                className={classes.selectSmall}
                variant="outlined"
                value={row.consciousness}
                onChange={(e) => handleRowChange(index, 'consciousness', e.target.value)}
                fullWidth
                disabled={loading}
              >
                {CONSCIOUSNESS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </div>
            <div style={{ width: '20%', paddingRight: 4 }}>
              <TextField
                className={classes.input}
                variant="outlined"
                size="small"
                placeholder="Notes..."
                value={row.notes}
                onChange={(e) => handleRowChange(index, 'notes', e.target.value)}
                fullWidth
                disabled={loading}
              />
            </div>
            <div style={{ width: '5%', textAlign: 'center' }}>
              {rows.length > 1 && (
                <Tooltip title="Remove reading">
                  <IconButton size="small" onClick={() => handleDeleteRow(index)} disabled={loading}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        ))}

        <Button
          className={classes.addBtn}
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          disabled={loading}
        >
          Add Reading
        </Button>
      </Paper>

      {/* Clinical Assessments */}
      <Paper className={classes.section} elevation={0}>
        <Typography className={classes.sectionTitle}>Clinical Assessment</Typography>
        <Grid container spacing={3} className={classes.assessmentGrid}>
          {/* Wound / Site Check */}
          <Grid item xs={12} sm={4}>
            <FormControl variant="outlined" size="small" fullWidth>
              <Label name="Wound / Site Check" />
              <Select
                label="Wound / Site Check"
                value={woundStatus}
                onChange={(e) => setWoundStatus(e.target.value)}
                disabled={loading}
              >
                {WOUND_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {woundStatus === 'Bleeding — escalate' && (
              <Typography style={{ fontSize: '0.78rem', color: '#C62828', fontWeight: 600, marginTop: 6 }}>
                Active bleeding detected — escalation recommended
              </Typography>
            )}
          </Grid>

          {/* Nausea / Vomiting */}
          <Grid item xs={12} sm={4}>
            <FormControl variant="outlined" size="small" fullWidth>
              <Label name="Nausea / Vomiting" />
              <Select
                label="Nausea / Vomiting"
                value={nauseaStatus}
                onChange={(e) => setNauseaStatus(e.target.value)}
                disabled={loading}
              >
                {NAUSEA_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Oral Fluid Tolerance */}
          <Grid item xs={12} sm={4}>
            <TextField
              variant="outlined"
              size="small"
              label="Oral Fluids Offered At"
              type="time"
              value={fluidOfferedTime}
              onChange={(e) => setFluidOfferedTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={loading}
            />
          </Grid>

          {fluidOfferedTime && (
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth>
                <Label name="Fluid Tolerance Result" />
                <Select
                  label="Fluid Tolerance Result"
                  value={fluidResult}
                  onChange={(e) => setFluidResult(e.target.value)}
                  disabled={loading}
                >
                  {FLUID_RESULT_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Escalation Warning */}
      {needsEscalation && (
        <div className={classes.escalateWarning}>
          <WarningIcon style={{ color: '#E65100' }} />
          <Typography style={{ fontSize: '0.88rem', fontWeight: 600, color: '#E65100' }}>
            Clinical findings may require escalation to inpatient care.
          </Typography>
        </div>
      )}

      {/* Save + Action Buttons */}
      <div style={{ marginTop: 16 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          style={{ marginBottom: 16 }}
        >
          Save Observations
        </Button>
      </div>

      <div className={classes.actionBtnRow}>
        <Button
          variant="contained"
          className={classes.dischargeBtn}
          startIcon={<CheckCircleIcon />}
          onClick={() => setReadyForDischarge(true)}
          disabled={loading || readyForDischarge}
        >
          {readyForDischarge ? 'Discharge Checklist Enabled' : 'Ready for Discharge'}
        </Button>
        <Button
          variant="contained"
          className={classes.escalateBtn}
          startIcon={<WarningIcon />}
          disabled={loading}
        >
          Escalate to Inpatient
        </Button>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DayCaseObservation;
